import { Box } from "@mui/system";
import { useParams } from "react-router-dom";
import { campaignDetailsTabList, unsavedDialogWarning } from "./constants";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import {
  GetCampaignById,
  GetCampaignSurveyById,
  GetSurveysInitial,
  GetTemplates,
  UpdateSurvey,
  resetCampaignDetails,
  selectCampaignInfo,
  selectCampaignSurveys,
  selectSelectedSurvey,
  selectSurveyInfo,
} from "store/slicers/campaignDetail";
import { Button, Typography, Theme, useMediaQuery } from "@mui/material";
import SharedDialog from "shared/ui/Dialog";
import { GlobalContext } from "App";
import { EAppReducerTypes } from "shared/helpers/AppContext";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { ERequestStatus } from "store/enums/index.enum";
import toast from "react-hot-toast";
import { ECampaignSurveyType } from "./questions/LeftSidebar/constants";
import {
  setCampaignLoading,
  setLoading,
  setSidebarVisible,
} from "store/slicers/common";
import { IUpdateSurveyRequest } from "store/interfaces/campaignDetails";
import CampaignTabs from "components/campaigns/CampaignTabs";
import EyeIcon from "@heroicons/react/24/outline/EyeIcon";
import QuestionPreview from "./components/QuestionPreview";
import { LStorage } from "store/config/constants";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { defaultFilterValues } from "resources/constants";

const defaultAnswer = {
  value: "",
  position: 0,
};

export const metricConfigable = [
  Number(ECampaignSurveyType.Nps),
  Number(ECampaignSurveyType.Rating),
  Number(ECampaignSurveyType.ServiceQualityScore),
  Number(ECampaignSurveyType.CustomerEffortScore),
  Number(ECampaignSurveyType.CustomStar),
  Number(ECampaignSurveyType.CustomerSatisfactionScore),
];

const answerResettable = [
  Number(ECampaignSurveyType.MultipleChoice),
  Number(ECampaignSurveyType.SingleChoice),
];

interface IFormData {
  surveyId: number;
  surveyType: number;
  title: string;
  answers: any[];
  isRequired: boolean;
  buttonText: string;
  skipText: string;
  commentText: string;
  multipleConfig?: {
    multipleType: number;
    multipleMin: string;
    multipleMax: string;
    multipleExact: string;
  };
  commentConfig?: {
    commentType: number;
    commentMin: string;
    commentMax: string;
  };
  metricConfig?: {
    metricRightText: string;
    metricLeftText: string;
    customStartLength: string;
    customEndLength: string;
  };
  contactConfig?: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
}

interface CampaignDetailsContextType {
  onSubmit: any;
  setAlreadySubmited: React.Dispatch<React.SetStateAction<boolean>>;
  refetchSurveyData?: () => Promise<void>;
}

export const CampaignDetailsContext = createContext<CampaignDetailsContextType>(
  {
    onSubmit: () => {},
    setAlreadySubmited: () => {},
    refetchSurveyData: async () => {},
  }
);

const CampaignDetail = () => {
  const t = useTranslation();
  const [isAlreadySubmited, setAlreadySubmited] = useState(false);

  const [tabValue, setTabValue] = useState(0);
  const surveyList = useSelector(selectCampaignSurveys);
  const selectedSurvey = useSelector(selectSelectedSurvey);
  const campaignInfo = useSelector(selectCampaignInfo);
  const { id } = useParams();
  const dispatch = useAsyncDispatch();
  const surveyDetails = useSelector(selectSurveyInfo);

  // Refs to always get the latest Redux values in async functions
  const surveyListRef = useRef(surveyList);
  const campaignInfoRef = useRef(campaignInfo);
  const selectedSurveyRef = useRef(selectedSurvey);
  
  // Keep refs updated with latest values
  surveyListRef.current = surveyList;
  campaignInfoRef.current = campaignInfo;
  selectedSurveyRef.current = selectedSurvey;
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const [isPreviewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewModalData, setPreviewModalData] = useState(null);
  const {
    contextInitialState: { campaignDetails },
    dispatchContext,
  } = useContext(GlobalContext);
  const smUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up("sm"));

  const methods = useForm<IFormData>({
    defaultValues: {
      surveyId: 0,
      surveyType: 0,
      answers: [defaultAnswer],
      title: "",
    },
  });

  // Watch form values to detect changes and reset submission state
  const methodValues = methods.watch();

  const handleSuccess = () => {
    dispatchContext({
      type: EAppReducerTypes.SET_UNSAVED_MODAL_DATA,
      payload: {
        isSuccess: true,
      },
    });
  };

  const handleSetOpen = (val) => {
    setUnsavedModalOpen(val);
    dispatchContext({
      type: EAppReducerTypes.SET_UNSAVED_MODAL_DATA,
      payload: {
        isOpen: true,
      },
    });
  };

  const handleSetClose = () => {
    dispatchContext({
      type: EAppReducerTypes.SET_UNSAVED_MODAL_DATA,
      payload: {
        isOpen: false,
      },
    });
  };

  const onUpdateSurvey = async (formData) => {
    if (!surveyDetails?.details) {
      return;
    }

    const currentSurveyList = surveyListRef.current;
    const currentCampaignInfo = campaignInfoRef.current;

    const position = currentSurveyList.find((i) => i.id === formData.surveyId)?.position;
    let answers = [];

    if (formData.answers?.length) {
      answers = formData?.answers?.map((answer) => {
        return {
          ...answer,
          newAnswer: !answer.id || false,
        };
      });
    }
    const config = {
      ...(formData.surveyType === Number(ECampaignSurveyType.Comment) && {
        commentConfig: formData?.commentConfig,
      }),
      ...(formData.surveyType === Number(ECampaignSurveyType.MultipleChoice) && {
        multipleConfig: formData?.multipleConfig,
      }),
      ...(metricConfigable.includes(formData.surveyType) && {
        metricConfig: formData?.metricConfig,
      }),
    };

    const data: IUpdateSurveyRequest = {
      campaignID: currentCampaignInfo?.id,
      languageID: currentCampaignInfo?.defaultLanguageID,
      title: formData.title,
      position,
      isRequired: formData.isRequired,
      buttonText: formData.buttonText,
      completed: true,
      ...(formData.isRequired
        ? { skipText: "" }
        : { skipText: formData.skipText }),
      ...(formData.surveyType === Number(ECampaignSurveyType.Comment)
        ? { commentText: formData.commentText }
        : { commentText: "" }),
      type: formData.surveyType,
      answers,
      ...config,
    };
    const { meta } = await dispatch(
      UpdateSurvey({
        data: data,
        id: formData.surveyId,
      })
    );
    return meta;
  };

  const onSubmit = async (formData: IFormData) => {
    const currentCampaignInfo = campaignInfoRef.current;
    
    await dispatch(setLoading(true));

    const meta = await onUpdateSurvey(formData);
    
    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      await dispatch(setLoading(false));
      return;
    }

    await Promise.all([
      dispatch(
        GetSurveysInitial({
          id: currentCampaignInfo.id,
          language_id: currentCampaignInfo.defaultLanguageID,
        })
      ),
      dispatch(
        GetCampaignSurveyById({
          id: formData.surveyId,
          language_id: currentCampaignInfo.defaultLanguageID,
        })
      ),
    ]);

    setAlreadySubmited(true);
    await dispatch(setLoading(false));
    toast.success("Campaign Saved successfully");
  };

  const refetchSurveyData = useCallback(async () => {
    const currentCampaignInfo = campaignInfoRef.current;
    const currentSurveyId = methods.getValues('surveyId');
    
    if (!currentSurveyId || !currentCampaignInfo?.defaultLanguageID) {
      return;
    }
    await Promise.all([
      dispatch(
        GetSurveysInitial({
          id: currentCampaignInfo.id,
          language_id: currentCampaignInfo.defaultLanguageID,
        })
      ),
      dispatch(
        GetCampaignSurveyById({
          id: currentSurveyId,
          language_id: currentCampaignInfo.defaultLanguageID,
        })
      ),
    ]);
  }, [dispatch, methods]);

  const init = useCallback(async () => {
    await dispatch(setCampaignLoading(true));
    if (id) {
      const res = await Promise.all([
        dispatch(GetCampaignById(Number(id))),
        dispatch(
          GetTemplates({
            formData: { ...defaultFilterValues, length: 8 },
            id: Number(id),
          })
        ),
      ]);

      await dispatch(
        GetSurveysInitial({
          id: Number(id),
          language_id: res[0]?.payload?.defaultLanguageID,
        })
      );

      await dispatch(setCampaignLoading(false));
      const selectedTab = localStorage.getItem(LStorage.SURVEY_TAB);
      if (selectedTab) {
        setTabValue(Number(selectedTab));
      }
    }
  }, [dispatch, id]);

  const handleResetForm = useCallback(() => {
    if (!surveyDetails?.details) {
      return;
    }

    methods.reset({
      surveyId: surveyDetails.details.id,
      surveyType: surveyDetails.details.type,
      title: surveyDetails.details?.title || "",
      answers:
        surveyDetails.details.answers?.length &&
        answerResettable.includes(surveyDetails.details.type)
          ? surveyDetails.details.answers
          : [defaultAnswer],
      metricConfig: {
        metricLeftText:
          surveyDetails.details?.metricConfig?.metricLeftText || "",
        metricRightText:
          surveyDetails.details?.metricConfig?.metricRightText || "",
        customStartLength:
          surveyDetails.details?.metricConfig?.customStartLength.toString() ||
          "",
        customEndLength:
          surveyDetails.details?.metricConfig?.customEndLength.toString() || "",
      },
      buttonText: surveyDetails.details.buttonText,
      skipText: surveyDetails.details?.skipText,
      commentText: surveyDetails.details?.commentText,
      isRequired: surveyDetails.details?.isRequired,
      multipleConfig: {
        multipleType: surveyDetails.details?.multipleConfig?.multipleType,
        multipleExact:
          surveyDetails.details?.multipleConfig?.multipleExact?.toString() ||
          "",
        multipleMin:
          surveyDetails.details?.multipleConfig?.multipleMin?.toString() || "",
        multipleMax:
          surveyDetails.details?.multipleConfig?.multipleMax?.toString() || "",
      },
      commentConfig: {
        commentType: surveyDetails.details?.commentConfig?.commentType,
        commentMin:
          surveyDetails.details?.commentConfig?.commentMin?.toString() || "",
        commentMax:
          surveyDetails.details?.commentConfig?.commentMax?.toString() || "",
      },
    });
    // methods is stable, so we can safely exclude it from dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyDetails?.details]);

  const onChange = (val) => {
    localStorage.setItem(LStorage.SURVEY_TAB, val.toString());
    setTabValue(val);
  };

  const openPreviewModal = () => {
    const watchValues = { ...methods.watch() };
    let data = {
      details: {
        ...surveyDetails?.details,
        title: watchValues.title,
        button: watchValues.buttonText,
      },
      template: { ...surveyDetails?.template },
    };
    if (
      surveyDetails?.details?.type === Number(ECampaignSurveyType.Rating) ||
      surveyDetails?.details?.type === Number(ECampaignSurveyType.Nps) ||
      surveyDetails?.details?.type ===
        Number(ECampaignSurveyType.ServiceQualityScore)
    ) {
      const {
        metricConfig: { customEndLength, customStartLength },
      } = watchValues;
      let answersArr = [];
      for (
        let i = Number(customStartLength);
        i <= Number(customEndLength);
        i++
      ) {
        answersArr.push({
          id: i,
          value: i,
        });
      }
      data = {
        details: {
          ...data?.details,
          metricConfig: watchValues?.metricConfig,
          answers: answersArr,
        },
        template: { ...data?.template },
      };
    }
    if (
      surveyDetails?.details?.type ===
        Number(ECampaignSurveyType.SingleChoice) ||
      surveyDetails?.details?.type ===
        Number(ECampaignSurveyType.MultipleChoice)
    ) {
      data = {
        details: {
          ...data?.details,
          answers: watchValues?.answers,
        },
        template: { ...data?.template },
      };
    }

    setPreviewModalData(data);
    setPreviewModalOpen(true);
  };

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (!isAlreadySubmited) {
      setUnsavedModalOpen(campaignDetails.isOpen);
    }
  }, [campaignDetails?.isOpen, isAlreadySubmited]);

  useEffect(() => {
    if (!surveyDetails?.details) {
      return;
    }

    handleResetForm();
  }, [handleResetForm, methods, surveyDetails?.details]);

  useLayoutEffect(() => {
    dispatch(setSidebarVisible(false));
    return () => {
      dispatch(resetCampaignDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    setAlreadySubmited(false);
  }, [methodValues]);

  return (
    <CampaignDetailsContext.Provider
      value={{
        onSubmit: onUpdateSurvey,
        setAlreadySubmited: setAlreadySubmited,
        refetchSurveyData: refetchSurveyData,
      }}
    >
      <Box>
        <FormProvider {...methods}>
          <Box display="flex">
            <CampaignTabs
              onChange={onChange}
              tabsData={campaignDetailsTabList}
              customStyles={{
                display: { xs: "block", sm: "flex" },
              }}
              Content={() => {
                if (tabValue === 0) {
                  return (
                    <Box
                      sx={{
                        position: {xs:'static',sm:'absolute'},
                        right: {xs:0,sm:10},
                        display: "flex",
                        gap: 1,
                        pt: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        onClick={openPreviewModal}
                        variant="outlined"
                        startIcon={<EyeIcon height={20} />}
                        size={smUp ? "medium" : "small"}
                      >
                        <Typography
                        >
                          {t("preview")}
                        </Typography>
                      </Button>

                      <Button
                        disabled={
                          !methods.formState.isDirty || isAlreadySubmited
                        }
                        onClick={methods.handleSubmit(onSubmit)}
                        variant="contained"
                        size={smUp ? "medium" : "small"}
                      >
                        <Typography
                        >
                          {t("save_changes")}
                        </Typography>
                      </Button>
                    </Box>
                  );
                }
                return null;
              }}
            />
          </Box>
        </FormProvider>
        <SharedDialog
          open={unsavedModalOpen}
          setOpen={handleSetOpen}
          onSuccess={handleSuccess}
          handleCloseCb={handleSetClose}
          textConfig={unsavedDialogWarning}
        />
        <SharedDialog
          hasActions={false}
          open={isPreviewModalOpen}
          setOpen={setPreviewModalOpen}
          fullScreen
          textConfig={{ title: "Preview" }}
          sx={{
            "& .MuiDialog-container": {
              "& .MuiDialogContent-root": {
                padding: 0,
              },
            },
          }}
        >
          <QuestionPreview
            methods={methods}
            previewModalData={previewModalData}
          />
        </SharedDialog>
      </Box>
    </CampaignDetailsContext.Provider>
  );
};

export default CampaignDetail;
